import { DiagramComponent } from '../../core/DiagramComponent';
import type { TextConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

interface ExtendedTextConfig extends TextConfig {
  fixed?: boolean;
  align?: string;
  valign?: string;
  layer?: number;
  attributes?: Record<string, any>;
}

interface JXGText extends JXGElement {
  setText(content: string): void;
}

/**
 * Text label component
 */
export class Text extends DiagramComponent<ExtendedTextConfig> {
  private _lastContent: string | number | boolean | null = null;

  constructor(config: ExtendedTextConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): JXGText {
    const config = this.config;
    const position = config.position || [0, 0];
    const content = this.evaluateExpression(config.content || '');

    const x = this.evaluateExpression(position[0]);
    const y = this.evaluateExpression(position[1]);

    const attributes = {
      fontSize: config.fontSize || 14,
      color: config.color || '#000',
      fixed: config.fixed !== false,
      anchorX: config.align || 'middle',
      anchorY: config.valign || 'middle',
      highlight: false, // Don't highlight on hover
      layer: config.layer || 8, // Lower layer than points
      cssClass: 'jxg-text-no-pointer', // Add CSS class for pointer-events
      ...config.attributes,
    };

    const text = this.board.create('text', [x, y, content], attributes) as JXGText;
    this.jsxElements.push(text);
    this._lastContent = content;

    return text;
  }

  /**
   * Update text content in place without recreating
   */
  protected _rerender(): void {
    const config = this.config;
    const newContent = this.evaluateExpression(config.content || '');
    
    // Skip if content hasn't changed
    if (newContent === this._lastContent) {
      return;
    }
    
    if (this.jsxElements.length > 0) {
      const text = this.jsxElements[0] as JXGText;
      text.setText(String(newContent));
      this._lastContent = newContent;
      this.board.update();
    } else {
      this.render();
    }
  }

  getSpecificity(): number {
    return 90; // Text has high priority (small bounded element)
  }
}
